import React, { useMemo, useState } from "react";
import {
  approveRegistration, fetchAgencies, fetchAgencyCois, fetchAttendanceForSession, fetchCertificationsByUserIds,
  fetchInstructorPendingSignOffs, fetchPendingRegistrationApprovals, fetchRegistrationsForClass, fetchSessionsForCourse,
  fetchTenantUsersByRole, rejectRegistration,
} from "../../../api/apiClient";
import { Num } from "../admin/adminCharts";
import useAsync from "../widgets/useAsync";
import { Bar, LoadFailed, Loading, daysUntil, fmtDate } from "../widgets/commonBodies";
import CloseIcon from "@mui/icons-material/Close";

const MAX_LEARNERS = 300;
const CHUNK = 100;
const chunk = (list, size) => Array.from({ length: Math.ceil(list.length / size) }, (_, i) => list.slice(i * size, (i + 1) * size));
const msg = (error) => error?.response?.data?.message || error?.message || "Request failed";

/** Enrollments waiting on coordinator approval, with approve / decline actions. */
export function RequestsBody() {
  const { loading, data, error } = useAsync(() => fetchPendingRegistrationApprovals(), []);
  const [done, setDone] = useState([]);
  const [failure, setFailure] = useState(null);
  const rows = useMemo(() => (data || []).filter((r) => !done.includes(r.id)), [data, done]);
  if (loading) return <Loading />;
  if (error) return <LoadFailed what="enrollment requests" />;
  const act = (fn, r) => fn(r.classId, r.id).then(() => { setDone((d) => [...d, r.id]); setFailure(null); }).catch((e) => setFailure(msg(e)));
  if (!rows.length) return <p className="wg-empty">No pending enrollment requests.</p>;
  return (
    <>
      {failure && <p className="wg-empty" role="alert">{failure}</p>}
      <div className="wl">{rows.slice(0, 6).map((r) => (
        <div key={r.id} className="wr" data-testid="enrollment-request">
          <div><b>{r.studentName || r.studentEmail || "Learner"}</b>
            <small>{r.classTitle}{r.courseNumber ? ` · ${r.courseNumber}` : ""}{r.prerequisitesMet === false && <span style={{ color: "var(--color-warn)", opacity: 1, fontWeight: 500 }}> · Prerequisites not met</span>}</small></div>
          <div className="wr-btns"><button type="button" className="go" onClick={() => act(approveRegistration, r)}>Approve</button>
            <button type="button" className="wg-x" aria-label={`Decline ${r.studentName || "request"}`} onClick={() => act(rejectRegistration, r)}><CloseIcon sx={{ fontSize: 14 }} /></button></div>
        </div>))}</div>
      {rows.length > 6 && <p className="wg-empty">+{rows.length - 6} more pending</p>}
    </>
  );
}

/** Skill sheets and tests waiting on each instructor's sign-off. */
export function GradingBody({ ctx }) {
  const instructors = ctx.raw.instructors.slice(0, 8);
  const { loading, data, error } = useAsync(() => Promise.all(instructors.map((i) => {
    const id = i.id || i.userId;
    return fetchInstructorPendingSignOffs(id).then((list) => ({ id, name: i.fullName || i.username || "Instructor", list: Array.isArray(list) ? list : [] })).catch(() => ({ id, name: i.fullName || i.username || "Instructor", list: [] }));
  })), [instructors.map((i) => i.id || i.userId).join(",")]);
  if (loading) return <Loading />;
  if (error) return <LoadFailed what="sign-offs" />;
  const rows = (data || []).filter((r) => r.list.length).sort((a, b) => b.list.length - a.list.length);
  const total = rows.reduce((s, r) => s + r.list.length, 0);
  if (!total) return <p className="wg-empty">Nothing waiting on sign-off.</p>;
  return (
    <>
      <div className="big"><Num to={total} /><small>skill sheets awaiting sign-off</small></div>
      <div className="wl">{rows.map((r) => (
        <div key={r.id} className="wr"><div><b>{r.name}</b><small>{r.list.length} skill sheet{r.list.length === 1 ? "" : "s"}</small></div>
          <span className={`tag ${r.list.length >= 5 ? "bad" : r.list.length >= 3 ? "warn" : "ok"}`}>{r.list.length}</span></div>))}</div>
    </>
  );
}

/** Learners (students) whose certifications expire in the next 30 days. */
export function ExpiringBody({ ctx }) {
  const { loading, data, error } = useAsync(async () => {
    const users = (await fetchTenantUsersByRole(ctx.tenantId, "STUDENT")) || [];
    const ids = users.map((u) => u.id).filter(Boolean).slice(0, MAX_LEARNERS);
    const byId = Object.fromEntries(users.map((u) => [u.id, u]));
    const batches = await Promise.all(chunk(ids, CHUNK).map((part) => fetchCertificationsByUserIds(part, ctx.tenantId).catch(() => [])));
    const horizon = daysUntil(new Date(+ctx.now + 30 * 86400000), ctx.now);
    return batches.flat().filter((c) => c.expires && daysUntil(c.expires, ctx.now) >= 0 && daysUntil(c.expires, ctx.now) <= horizon)
      .map((c) => ({ ...c, days: daysUntil(c.expires, ctx.now), user: byId[c.userId] }))
      .sort((a, b) => a.days - b.days);
  }, [ctx.tenantId]);
  if (loading) return <Loading />;
  if (error) return <LoadFailed what="learner certifications" />;
  if (!data.length) return <p className="wg-empty">No learner certifications expire in the next 30 days.</p>;
  return (
    <table className="wtb"><thead><tr><th>Learner</th><th>Certification</th><th className="hm">Expires</th><th>Renewal</th></tr></thead>
      <tbody>{data.slice(0, 8).map((c) => <tr key={c.id}><td>{c.user?.fullName || c.user?.username || "Learner"}<small>{c.user?.email}</small></td><td>{c.name}</td><td className="hm mo">{fmtDate(c.expires)}</td>
        <td><span className={`tag ${c.days <= 14 ? "bad" : "warn"}`}>{c.days} days</span></td></tr>)}</tbody></table>
  );
}

/** Latest certificate of insurance per agency partner. */
export function CoiBody({ ctx }) {
  const { loading, data, error } = useAsync(async () => {
    const agencies = ((await fetchAgencies(ctx.tenantId)) || []).slice(0, 10);
    return Promise.all(agencies.map(async (a) => {
      const cois = (await fetchAgencyCois(ctx.tenantId, a.id).catch(() => [])) || [];
      const latest = [...cois].sort((x, y) => String(y.validTo || "").localeCompare(String(x.validTo || "")))[0] || null;
      return { id: a.id, name: a.name, coi: latest };
    }));
  }, [ctx.tenantId]);
  if (loading) return <Loading />;
  if (error) return <LoadFailed what="certificates of insurance" />;
  if (!data.length) return <p className="wg-empty">No certificates of insurance on file.</p>;
  const rank = { expired: 0, missing: 0, expiring_soon: 1, active: 2 };
  const status = (r) => (r.coi ? r.coi.status : "missing");
  return (
    <div className="wl">{[...data].sort((a, b) => rank[status(a)] - rank[status(b)]).slice(0, 8).map((r) => {
      const s = status(r);
      return <div key={r.id} className="wr"><div><b>{r.name}</b><small>{r.coi ? `${s === "expired" ? "Expired" : s === "expiring_soon" ? "Expires" : "Valid to"} ${fmtDate(r.coi.validTo)}` : "No certificate on file"}</small></div>
        <span className={`tag ${s === "active" ? "ok" : s === "expiring_soon" ? "warn" : "bad"}`}>{s === "active" ? "Current" : s === "expiring_soon" ? "Expiring" : s === "expired" ? "Expired" : "Missing"}</span></div>;
    })}</div>
  );
}

/** Check-ins for the session running right now (first live session in this week's schedule). */
export function RosterBody({ ctx }) {
  const live = ctx.m.week.days.find((d) => d.today)?.sessions.find((s) => s.live) || null;
  const { loading, data, error } = useAsync(async () => {
    if (!live) return null;
    const [sessions, regs] = await Promise.all([fetchSessionsForCourse(live.courseId), fetchRegistrationsForClass(live.courseId)]);
    const today = new Date(ctx.now).toISOString().slice(0, 10);
    const session = (sessions || []).find((s) => s.sessionDate === today && String(s.startTime || "").slice(0, 5) === live.time) || (sessions || []).find((s) => s.sessionDate === today);
    const attendance = session ? (await fetchAttendanceForSession(session.id)) || [] : [];
    const present = new Set(attendance.filter((a) => a.attended || a.checkedInAt).map((a) => a.userId));
    const enrolled = (regs || []).filter((r) => r.enrollmentStatus === "ENROLLED");
    return { session, enrolled, present };
  }, [live?.id]);
  if (!live) return <p className="wg-empty">No session is running right now.</p>;
  if (loading) return <Loading />;
  if (error || !data) return <LoadFailed what="the roster" />;
  const total = data.enrolled.length;
  const here = data.enrolled.filter((r) => data.present.has(r.userId)).length;
  const missing = data.enrolled.filter((r) => !data.present.has(r.userId));
  return (
    <>
      <div className="wtop"><div><b>{live.name}</b><small>{live.time}{live.meta ? ` · ${live.meta}` : ""}</small></div><span className="tag live">In session</span></div>
      <div className="big"><Num to={here} /><small>of {total} checked in</small></div>
      <Bar pct={total ? here / total * 100 : 0} color="var(--color-accent-violet)" />
      {missing.length > 0 && <div className="wl">{missing.slice(0, 4).map((r) => <div key={r.id} className="wr"><div><b>{r.fullName || r.name || `Learner ${r.userId}`}</b><small>No check-in</small></div>
        <button type="button" className="go" onClick={() => ctx.navigate(`/course-hub/${live.courseId}`)}>Roster</button></div>)}</div>}
    </>
  );
}
